import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  container: {
    marginBottom: 10,
  },
  competencyGrid: {
    flexDirection: 'column',
    gap: 3,
    alignItems: 'flex-start',
    padding: 6,
    backgroundColor: '#f9f9f9',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  phaseGroup: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 1,
    marginRight: 0,
    marginBottom: 4,
    width: '100%',
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    margin: 0.7,
  },
  legend: {
    marginTop: 6,
    padding: 4,
    backgroundColor: '#f5f5f5',
    borderRadius: 3,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  legendRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 2,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 1,
  },
  legendDot: {
    width: 4,
    height: 4,
    borderRadius: 2,
    marginRight: 2,
  },
  legendText: {
    fontSize: 5,
  },
  statusLegend: {
    marginTop: 2,
    paddingTop: 2,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  emptyMessage: {
    fontSize: 8,
    textAlign: 'center',
    color: '#888888',
    fontStyle: 'italic',
    padding: 10,
  }
});

// Category colors for the dots
const categoryColors = {
  skills: '#4CAF50',      // Green
  knowledge: '#2196F3',   // Blue  
  mindset: '#FF9800',     // Orange
  instruments: '#9C27B0', // Purple
};

const CinemaMapPDF = ({ allCompetenciesData, sessionResponses = [] }) => {
  // Get competency IDs that were selected in this session
  const selectedCompetencyIds = useMemo(() => {
    return new Set(sessionResponses?.map(response => response.competency_id) || []);
  }, [sessionResponses]);

  // Process all competencies and group by phase (same logic as CinemaMapWeb)
  const competenciesByPhase = useMemo(() => {
    if (!allCompetenciesData) return {};

    const phases = ['Empathize', 'Define', 'Ideate', 'Prototype', 'Test', 'Communicate'];
    const grouped = {};

    // Initialize each phase
    phases.forEach(phase => {
      grouped[phase] = [];
    });

    // Group competencies by phase
    allCompetenciesData.forEach(comp => {
      const phase = comp.abilities?.phase || 'Unknown';
      const competency = {
        id: comp.id,
        name: comp.name,
        category: comp.category,
        phase: phase,
        ability: comp.abilities?.name || 'Unknown',
        isSelected: selectedCompetencyIds.has(comp.id)
      };

      if (grouped[phase]) {
        grouped[phase].push(competency);
      } else {
        // Handle unknown phases
        if (!grouped['Unknown']) grouped['Unknown'] = [];
        grouped['Unknown'].push(competency);
      }
    });

    return grouped;
  }, [allCompetenciesData, selectedCompetencyIds]);

  const renderDots = (competencies) => {
    return competencies.map((competency, index) => {
      const categoryColor = categoryColors[competency.category] || '#666666';
      const dotColor = competency.isSelected ? categoryColor : '#ccc'; // Darker grey for unselected
      
      return (
        <View
          key={`${competency.id}-${index}`}
          style={[
            styles.dot,
            {
              backgroundColor: dotColor,
            }
          ]}
        />
      );
    });
  };

  if (!allCompetenciesData || allCompetenciesData.length === 0) {
    return (
      <View style={styles.container}>
        <Text style={styles.emptyMessage}>
          No competencies available in the system
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* All competencies in one compact grid, grouped by phases with spacing */}
      <View style={styles.competencyGrid}>
        {Object.entries(competenciesByPhase).map(([phase, competencies]) => {
          if (!competencies || competencies.length === 0) return null;
          
          return (
            <View key={phase} style={styles.phaseGroup}>
              {renderDots(competencies)}
            </View>
          );
        })}
      </View>

      {/* Compact legend */}
      <View style={styles.legend}>
        {/* Category colors */}
        <View style={styles.legendRow}>
          {Object.entries(categoryColors).map(([category, color]) => (
            <View key={category} style={styles.legendItem}>
              <View style={[styles.legendDot, { backgroundColor: color }]} />
              <Text style={styles.legendText}>
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </Text>
            </View>
          ))}
          
          {/* Selection status in same row */}
          <View style={styles.legendItem}>
            <View style={[styles.legendDot, { backgroundColor: '#4CAF50' }]} />
            <Text style={styles.legendText}>Selected</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendDot, { backgroundColor: '#ccc' }]} />
            <Text style={styles.legendText}>Not selected</Text>
          </View>
        </View>
      </View>
    </View>
  );
};

export default CinemaMapPDF;
